import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scheduleAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Calendar, Filter, ChevronLeft, ChevronRight, Trash2, MoreVertical, Edit } from 'lucide-react';
import { format } from 'date-fns';
import { formatLocal } from '../lib/dateUtils';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const eventTypeColors = {
  event: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  task: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  job: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  missed: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

const priorityColors = {
  low: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  medium: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  high: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  urgent: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

export default function EventsList() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    event_type: 'all',
    status: 'all',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    loadEvents();
  }, [page, filters]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const params = {
        event_type: filters.event_type !== 'all' ? filters.event_type : undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
      };

      const response = await scheduleAPI.getEvents(params);
      let allEvents = response.data;

      // Filter by event type to show only event and task (not follow_up, visit)
      allEvents = allEvents.filter(event => 
        event.event_type === 'event' || event.event_type === 'task'
      );

      // Client-side search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        allEvents = allEvents.filter(event =>
          event.title?.toLowerCase().includes(searchLower) ||
          event.description?.toLowerCase().includes(searchLower) ||
          event.location?.toLowerCase().includes(searchLower)
        );
      }

      // Sort by updated_at or created_at (newest first)
      allEvents.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA;
      });

      // Pagination
      const total = Math.ceil(allEvents.length / itemsPerPage);
      setTotalPages(total);
      const startIdx = (page - 1) * itemsPerPage;
      const paginatedEvents = allEvents.slice(startIdx, startIdx + itemsPerPage);

      setEvents(paginatedEvents);
    } catch (error) {
      console.error('Failed to load events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page on filter change
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      event_type: 'all',
      status: 'all',
      start_date: '',
      end_date: '',
    });
    setPage(1);
  };

  const handleStatusChange = async (eventId, newStatus) => {
    try {
      await scheduleAPI.updateEvent(eventId, { status: newStatus });
      toast.success('Status updated successfully', {
        className: 'bg-green-50 text-green-900 border-green-200',
      });
      loadEvents();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status', {
        className: 'bg-red-50 text-red-900 border-red-200',
      });
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEditForm({
      title: event.title || '',
      description: event.description || '',
      location: event.location || '',
      start_time: event.start_time ? new Date(event.start_time).toISOString().slice(0, 16) : '',
      end_time: event.end_time ? new Date(event.end_time).toISOString().slice(0, 16) : '',
      priority: event.priority || 'medium',
      status: event.status || 'pending',
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.title || !editForm.start_time || !editForm.end_time) {
      toast.error('Please fill in all required fields', {
        className: 'bg-red-50 text-red-900 border-red-200',
      });
      return;
    }

    try {
      await scheduleAPI.updateEvent(editingEvent.id, {
        ...editForm,
        start_time: new Date(editForm.start_time).toISOString(),
        end_time: new Date(editForm.end_time).toISOString(),
      });
      toast.success('Event updated successfully', {
        className: 'bg-green-50 text-green-900 border-green-200',
      });
      setEditDialogOpen(false);
      loadEvents();
    } catch (error) {
      console.error('Failed to update event:', error);
      toast.error('Failed to update event', {
        className: 'bg-red-50 text-red-900 border-red-200',
      });
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleDelete = async (eventId, eventTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${eventTitle}"?`)) {
      return;
    }

    try {
      await scheduleAPI.deleteEvent(eventId);
      toast.success('Event deleted successfully');
      loadEvents();
    } catch (error) {
      console.error('Failed to delete event:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete event';
      toast.error(errorMsg);
    }
  };

  return (
    <div className="space-y-6" data-testid="events-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">All Events</h1>
          <p className="text-muted-foreground mt-1">
            View and manage your events and tasks
          </p>
        </div>
        <Button onClick={() => navigate('/schedule')}>
          <Calendar className="h-4 w-4 mr-2" />
          View Calendar
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Input
              placeholder="Search events..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
            
            <Select
              value={filters.event_type}
              onValueChange={(value) => handleFilterChange('event_type', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Event Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="event">Event</SelectItem>
                <SelectItem value="task">Task</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.status}
              onValueChange={(value) => handleFilterChange('status', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="missed">Missed</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              placeholder="Start Date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
            />

            <Input
              type="date"
              placeholder="End Date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
            />
          </div>
          <div className="mt-4">
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Events Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading events...</div>
          ) : events.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No events found
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Start Time</TableHead>
                    <TableHead>End Time</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell 
                        className="font-medium cursor-pointer hover:underline"
                        onClick={() => navigate('/schedule')}
                      >
                        {event.title}
                      </TableCell>
                      <TableCell>
                        <Badge className={eventTypeColors[event.event_type]}>
                          {event.event_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {formatLocal(event.start_time, 'MMM dd, yyyy HH:mm')}
                      </TableCell>
                      <TableCell>
                        {formatLocal(event.end_time, 'MMM dd, yyyy HH:mm')}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={statusColors[event.status]}>
                          {event.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={priorityColors[event.priority] || priorityColors.medium}>
                          {event.priority || 'medium'}
                        </Badge>
                      </TableCell>
                      <TableCell>{event.location || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Edit Icon */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleEditEvent(event)}
                            title="Edit Event"
                          >
                            <Edit className="h-4 w-4 text-blue-600" />
                          </Button>
                          
                          {/* Delete Icon */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleDelete(event.id, event.title)}
                            title="Delete Event"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                          
                          {/* Kebab Menu for Status Changes */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Change Status">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleStatusChange(event.id, 'pending')}>
                                Mark as Pending
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusChange(event.id, 'completed')}>
                                Mark as Completed
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusChange(event.id, 'cancelled')}>
                                Mark as Cancelled
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusChange(event.id, 'missed')}>
                                Mark as Missed
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Event Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Event</DialogTitle>
            <DialogDescription>
              Update the event details below
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-title">Title *</Label>
              <Input
                id="edit-title"
                value={editForm.title || ''}
                onChange={(e) => handleEditFormChange('title', e.target.value)}
                placeholder="Event title"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editForm.description || ''}
                onChange={(e) => handleEditFormChange('description', e.target.value)}
                placeholder="Event description"
                rows={3}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-location">Location</Label>
              <Input
                id="edit-location"
                value={editForm.location || ''}
                onChange={(e) => handleEditFormChange('location', e.target.value)}
                placeholder="Event location"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-start-time">Start Time *</Label>
                <Input
                  id="edit-start-time"
                  type="datetime-local"
                  value={editForm.start_time || ''}
                  onChange={(e) => handleEditFormChange('start_time', e.target.value)}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit-end-time">End Time *</Label>
                <Input
                  id="edit-end-time"
                  type="datetime-local"
                  value={editForm.end_time || ''}
                  onChange={(e) => handleEditFormChange('end_time', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-priority">Priority</Label>
                <Select
                  value={editForm.priority || 'medium'}
                  onValueChange={(value) => handleEditFormChange('priority', value)}
                >
                  <SelectTrigger id="edit-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit-status">Status</Label>
                <Select
                  value={editForm.status || 'pending'}
                  onValueChange={(value) => handleEditFormChange('status', value)}
                >
                  <SelectTrigger id="edit-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="missed">Missed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
